import { useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { FixedSizeList } from "react-window";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

import {
  Box,
  Grid,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  TableBody,
  Tooltip,
  Typography,
  Badge,
  useTheme
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faCheck,
  faHourglassStart,
  faLock
} from "@fortawesome/free-solid-svg-icons";
import {
  faBuilding,
  faUserFriends,
  faMoneyBill
} from "@fortawesome/pro-solid-svg-icons";

import { Checkbox } from "@aclymatepackages/atoms";
import {
  formatDate,
  formatDecimal,
  formatDollars,
  letterSBoolean,
  ucFirstLetters
} from "@aclymatepackages/formatters";
import {
  addDataRow,
  removeDataRow
} from "@aclymatepackages/array-immutability-helpers";
import { hexToRgba } from "@aclymatepackages/converters";
import { truncateText } from "@aclymatepackages/other-helpers";

import NullableInput from "../../../../atoms/inputs/NullableInput";
import ViewRowTransactionDetails from "../../ViewRowTransactionDetails";

import EmissionsSourcesIcon from "../../../../atoms/icons/EmissionSourcesIcon";
import TagRowDisplay from "../../../../atoms/icons/TagRowDisplay";

import FilterPopper from "../../../../modules/filter/FilterPopper";

import EmissionsVolumeForm from "../../../../inputs/emissions/EmissionsVolumeForm";
import { useUpdateTransactions } from "../../../../../helpers/components/emissions";
import useDisplayMeasurementSystem from "../../../../../helpers/hooks/displayMeasurementSystem";

dayjs.extend(isSameOrBefore);

const CHECKBOX_WIDTH = 48;
const SCROLLBAR_WIDTH = 15;
const TABLE_HEIGHT = 481;

const emissionStatuses = {
  confirmed: {
    icon: faCheck,
    color: "secondary",
    tooltip: "This emission is confirmed and added to your balance"
  },
  locked: {
    icon: faLock,
    color: "backgroundGray",
    tooltip:
      "This emission is locked because you've closed the accounting period for it"
  },
  unconfirmed: {
    icon: faEdit,
    color: "error",
    tooltip:
      "This emission has been added to your balance but is missing information to make it more accurate"
  },
  cleared: {
    icon: faHourglassStart,
    color: "travel",
    tooltip:
      "This emission hasn't been added to your balance yet because it hasn't happened yet"
  }
};

const getAmount = (row, field) => (row[field] === undefined ? 0 : row[field]);

const getName = (row, field) =>
  row[field] === undefined ? row.displayName || "" : row[field];

const getTonsCo2e = (row, field) => (row[field] == null ? 0 : row[field]);

const getDate = (row, field) => {
  const value = row[field];
  return value instanceof Date ? value : new Date(value);
};

const compareValues = (a, b) => (a > b ? -1 : a < b ? 1 : 0);

const sortAndOrderRows = (rows, { field, isAsc }) => {
  const adjustedField = field === "status" ? "severity" : field;

  const sortedRows = [...rows].sort((a, b) => {
    switch (field) {
      case "value":
        return compareValues(
          getAmount(a, adjustedField),
          getAmount(b, adjustedField)
        );
      case "name":
        return compareValues(
          getName(a, adjustedField),
          getName(b, adjustedField)
        );
      case "date":
        return compareValues(
          getDate(a, adjustedField),
          getDate(b, adjustedField)
        );
      case "tonsCo2e":
        return compareValues(
          getTonsCo2e(a, adjustedField),
          getTonsCo2e(b, adjustedField)
        );
      default:
        return compareValues(a[adjustedField], b[adjustedField]);
    }
  });

  return isAsc ? sortedRows.reverse() : sortedRows;
};

const ActionsTransactionsBlock = ({
  tonsCo2e,
  transactionId,
  section,
  acceptDisabled,
  disabled,
  setEmissions,
  updateTransactions
}) => {
  const { palette } = useTheme();

  const generateAcceptIconTitle = () => {
    if (section === "all-emissions") {
      return "Accept default value for this transaction";
    }

    if (section === "reject-emissions" && !tonsCo2e) {
      return "Add more information to this transaction to add it to your balance";
    }

    return "Add transaction to balance";
  };

  return (
    <Grid container wrap="nowrap">
      <Grid item>
        <Tooltip
          title={generateAcceptIconTitle()}
          disableHoverListener={acceptDisabled || !tonsCo2e}
        >
          <span>
            <IconButton
              disabled={acceptDisabled || !tonsCo2e}
              onClick={(e) => {
                updateTransactions([
                  { id: transactionId, status: "confirmed" }
                ]);
                setEmissions((prevEmissions) =>
                  prevEmissions.map((emission) =>
                    emission.id === transactionId
                      ? { ...emission, status: "confirmed" }
                      : emission
                  )
                );

                return e.stopPropagation();
              }}
              size="large"
            >
              <CheckCircleIcon
                fontSize="small"
                style={{
                  color:
                    acceptDisabled || !tonsCo2e
                      ? "inherit"
                      : palette.success.main
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      </Grid>
      <Grid item>
        <Tooltip
          title="Archive Transaction"
          disableHoverListener={disabled ? disabled : false}
        >
          <span>
            <IconButton
              disabled={disabled ? disabled : false}
              onClick={(e) => {
                updateTransactions([{ id: transactionId, archived: true }]);
                setEmissions((prevEmissions) =>
                  prevEmissions.map((emission) =>
                    emission.id === transactionId
                      ? { ...emission, archived: true }
                      : emission
                  )
                );

                return e.stopPropagation();
              }}
              size="large"
            >
              <DeleteIcon
                fontSize="small"
                style={{
                  color: disabled ? "inherit" : palette.error.main
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      </Grid>
    </Grid>
  );
};

const VirtualizedList = ({
  height,
  width,
  itemCount,
  itemSize,
  className,
  style,
  rowRenderFunc
}) => {
  const otherProps = {
    height,
    width,
    itemCount,
    itemSize,
    className,
    style
  };
  return <FixedSizeList {...otherProps}>{rowRenderFunc}</FixedSizeList>;
};

const VirtualizedListCellComponent = ({
  column,
  width,
  align = "left",
  style: { height },
  children,
  findColumnWidth
}) => {
  const columnWidth = width ? `${width}px` : `${findColumnWidth(column)}px`;

  return (
    <TableCell
      component="div"
      style={{
        width: columnWidth,
        minWidth: columnWidth,
        maxWidth: columnWidth,
        flexShrink: 0,
        flexGrow: 0,
        height: height,
        maxHeight: height,
        boxSizing: "border-box",
        textOverflow: "ellipsis",
        overflow: "hidden"
      }}
      align={align}
    >
      {children}
    </TableCell>
  );
};

const TableHeaderCell = ({
  annotation,
  field,
  label,
  isColumnActive,
  onSortClick,
  isAsc,
  style,
  headerCheckboxProps
}) => {
  const theme = useTheme();

  const createTableLabel = () =>
    onSortClick ? (
      <TableSortLabel
        active={isColumnActive}
        direction={isAsc ? "asc" : "desc"}
        onClick={onSortClick}
        component="div"
      >
        {(label || field)?.toUpperCase()}
      </TableSortLabel>
    ) : (
      <Typography variant="inherit">
        {(label || field)?.toUpperCase()}
      </Typography>
    );

  return (
    <TableCell component="div" style={style}>
      {!!annotation && (
        <Tooltip title={annotation}>
          <HelpOutlineIcon
            color="action"
            style={{
              verticalAlign: "middle",
              paddingBottom: 2,
              marginRight: theme.spacing(0.5)
            }}
            fontSize="small"
          />
        </Tooltip>
      )}
      {field === "checkbox" ? (
        <Checkbox {...headerCheckboxProps} />
      ) : (
        createTableLabel()
      )}
    </TableCell>
  );
};

const TableHeader = ({
  columns,
  width,
  findColumnWidth,
  selectedSort = {},
  sortRows,
  headerCheckboxProps
}) => {
  const { field: sortField, isAsc } = selectedSort;
  const { palette } = useTheme();

  const headerCellStyles = (field) => {
    const columnWidth = findColumnWidth(field);
    return {
      flexBasis: field ? `${columnWidth}px` : CHECKBOX_WIDTH,
      display: "flex",
      flexGrow: 0,
      flexShrink: 0,
      boxSizing: "border-box",
      fontWeight: 600,
      border: "none",
      flexWrap: "nowrap",
      alignItems: "center",
      overflow: "hidden"
    };
  };

  return (
    <TableHead
      component="div"
      style={{
        width: `${width}px`,
        display: "block",
        backgroundColor: palette.backgroundGray.light
      }}
    >
      <TableRow
        component="div"
        style={{
          display: "flex",
          flexWrap: "nowrap",
          alignItems: "center",
          width: `${width}px`
        }}
      >
        {columns.map((column, idx) => (
          <TableHeaderCell
            key={`table-header-cell-${idx}`}
            style={headerCellStyles(column.field)}
            isColumnActive={column.field === sortField}
            isAsc={isAsc}
            onSortClick={sortRows && (() => sortRows(column.field))}
            headerCheckboxProps={headerCheckboxProps}
            {...column}
          />
        ))}
      </TableRow>
    </TableHead>
  );
};

const VirtualizedTableRow = ({
  findColumnWidth,
  style,
  onClick,
  idx,
  tableCells
}) => {
  const getCellComponentProps = (column) => ({
    column,
    style,
    findColumnWidth
  });

  return (
    <TableRow
      style={{
        ...style,
        display: "flex",
        flexWrap: "nowrap",
        alignItems: "center",
        cursor: onClick ? "pointer" : "default"
      }}
      component="div"
      onClick={onClick ? () => onClick() : null}
      hover={!!onClick}
    >
      {tableCells.map(({ value, field, style }) => (
        <VirtualizedListCellComponent
          style={style}
          key={`table-row-${idx}-${field}`}
          {...getCellComponentProps(field)}
        >
          {value}
        </VirtualizedListCellComponent>
      ))}
    </TableRow>
  );
};

const VirtualizedTable = ({
  tableTitle,
  tableAction,
  width,
  rows,
  RowComponent,
  selectedTransaction,
  setSelectedTransaction,
  setSelectedEmission,
  setSelectedRecurringEmission,
  tableColumns,
  selectedSort,
  sortRows,
  isAsc,
  rowProps = {},
  headerCheckboxProps = {},
  viewMode,
  tableRef
}) => {
  const [columnWidths, setColumnWidths] = useState([]);

  useEffect(() => {
    const calculateColumnWidths = () => {
      const minWidthSum = tableColumns.reduce(
        (sum, { minWidth }) => Number(sum) + Number(minWidth),
        0
      );

      const scaledWidths = tableColumns.map((row) => ({
        ...row,
        minWidth: (row.minWidth / minWidthSum) * (width - SCROLLBAR_WIDTH)
      }));

      const flooredWidths = scaledWidths.map(({ minWidth, ...rest }) => ({
        ...rest,
        minWidth: Math.floor(minWidth)
      }));

      const remainder =
        width -
        SCROLLBAR_WIDTH -
        flooredWidths.reduce((sum, { minWidth }) => sum + minWidth, 0);

      const sortedByFraction = [...flooredWidths]
        .map((col, idx) => ({
          idx,
          fraction:
            scaledWidths[idx].minWidth - Math.floor(scaledWidths[idx].minWidth)
        }))
        .sort((a, b) => b.fraction - a.fraction);

      const bumpedIndexes = new Set(
        sortedByFraction.slice(0, remainder).map(({ idx }) => idx)
      );

      const columnWidths = flooredWidths.map((col, idx) => ({
        ...col,
        minWidth: col.minWidth + (bumpedIndexes.has(idx) ? 1 : 0)
      }));

      setColumnWidths(columnWidths);
    };

    if (width) {
      calculateColumnWidths();
    }
  }, [tableColumns, width]);

  const findColumnWidth = (field) => {
    return (
      Array.isArray(columnWidths) &&
      columnWidths.find((row) => row.field === field)?.minWidth
    );
  };

  const VirtualizedTableRow = ({ index, style }) => {
    const rowData = rows[index];

    return (
      <RowComponent
        viewMode={viewMode}
        findColumnWidth={findColumnWidth}
        emission={rowData}
        style={style}
        selectedTransaction={selectedTransaction}
        setSelectedTransaction={setSelectedTransaction}
        setSelectedEmission={setSelectedEmission}
        setSelectedRecurringEmission={setSelectedRecurringEmission}
        idx={index}
        {...rowProps}
      />
    );
  };

  return (
    <>
      <Box mx={2} ref={tableRef}>
        <Grid container justifyContent="space-between">
          <Grid item>
            <Grid container spacing={2}>
              <Grid item>
                <Typography variant="h6">{tableTitle}</Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Grid container alignItems="center">
              <Grid item>{tableAction}</Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <Table
        style={{ width, minWidth: width, display: "block" }}
        component="div"
      >
        <TableHeader
          columns={columnWidths}
          width={width}
          findColumnWidth={findColumnWidth}
          selectedSort={selectedSort}
          sortRows={sortRows}
          isAsc={isAsc}
          headerCheckboxProps={headerCheckboxProps}
        />
        <TableBody component="div" style={{ display: "block" }}>
          <VirtualizedList
            height={TABLE_HEIGHT}
            width={width}
            itemCount={rows.length}
            itemSize={80}
            rowRenderFunc={VirtualizedTableRow}
          />
        </TableBody>
      </Table>
    </>
  );
};

const EmissionsTableRowComponent = ({
  viewMode,
  style,
  findColumnWidth,
  emission,
  setSelectedTransaction,
  setSelectedEmission,
  setSelectedRecurringEmission,
  idx,
  selectedTransactionIds,
  onTransactionCheck,
  areAnyTransactionsNonRecurring,
  setEmissions,
  updateTransactions
}) => {
  const {
    date,
    name,
    displayName,
    subcategory,
    status,
    tonsCo2e,
    id,
    source,
    office,
    value,
    amount,
    dollarAmount,
    notes = []
  } = emission || {};

  const { convertCarbonUnits } = useDisplayMeasurementSystem();
  const { palette } = useTheme();

  const valueToUse = value || dollarAmount || amount;

  const isManualEntryEmission = source === "manual-emission";

  const {
    icon: statusIcon,
    color: statusColor,
    tooltip: statusTooltip
  } = emissionStatuses[status] || {};

  const isTransactionCheckable = !(
    ["default-utilities", "recurring"].includes(source) ||
    subcategory === "events"
  );

  const buildNameString = () => {
    if (name) {
      return name;
    }

    if (source === "manual-entry" && office) {
      return `${displayName} (${office.name})`;
    }

    if (displayName) {
      return truncateText(displayName, 80);
    }

    return "N/A";
  };

  const checkBoxForUnconfirmedEmissions = areAnyTransactionsNonRecurring
    ? [
        {
          value: (
            <Tooltip
              disableHoverListener={isTransactionCheckable}
              title="You can only select unconfirmed transactions."
            >
              <span>
                <Checkbox
                  disabled={
                    !isTransactionCheckable ||
                    source === "manual-emission" ||
                    subcategory === "events"
                  }
                  value={selectedTransactionIds.includes(id)}
                  editValue={() => onTransactionCheck(id)}
                />
              </span>
            </Tooltip>
          ),
          field: "checkbox"
        }
      ]
    : [];

  const actionsForUnconfirmedEmissions = areAnyTransactionsNonRecurring
    ? [
        {
          value: (
            <ActionsTransactionsBlock
              tonsCo2e={tonsCo2e}
              transactionId={id}
              section="all-emissions"
              acceptDisabled={
                status === "confirmed" ||
                subcategory === "events" ||
                status === "locked"
              }
              disabled={
                !isTransactionCheckable ||
                subcategory === "events" ||
                ["manual-emission", "recurring-closed"].includes(source) ||
                status === "locked"
              }
              setEmissions={setEmissions}
              updateTransactions={updateTransactions}
            />
          ),
          field: "actions"
        }
      ]
    : [];

  const tableCells = [
    ...checkBoxForUnconfirmedEmissions,
    {
      value: formatDate(date),
      field: "date"
    },
    {
      value: (
        <Tooltip title={statusTooltip}>
          <div>
            <FontAwesomeIcon
              icon={statusIcon}
              size="lg"
              style={{ color: palette[statusColor]?.main }}
            />
          </div>
        </Tooltip>
      ),
      field: "status"
    },
    {
      value: <EmissionsSourcesIcon emission={emission} />,
      field: "source"
    },
    {
      field: viewMode === "subcategories" ? "subcategory" : "scope",
      value: <TagRowDisplay {...emission} viewMode={viewMode} />,
      clickable: false
    },
    { field: "value", value: valueToUse ? formatDollars(valueToUse) : "N/A" },
    {
      value: buildNameString(),
      field: "name"
    },
    {
      value: !!notes.length && (
        <Tooltip title={`${notes.length} note${notes.length > 1 ? "s" : ""}`}>
          <StickyNote2Icon fontSize="small" color="action" />
        </Tooltip>
      ),
      field: "notes"
    },
    {
      value: (
        <Grid container alignItems="center" spacing={1}>
          <Grid item>
            {tonsCo2e
              ? formatDecimal(Math.abs(convertCarbonUnits(tonsCo2e)))
              : "N/A"}
          </Grid>
          {status === "unconfirmed" && subcategory !== "spend-based" && (
            <Grid item>
              <Tooltip
                title="This is the default value we estimated for this transaction. You can either provide more information about this transaction or accept the default value"
                style={{ color: palette.error.main }}
              >
                <HelpOutlineIcon />
              </Tooltip>
            </Grid>
          )}
        </Grid>
      ),
      field: "tonsCo2e"
    },
    ...actionsForUnconfirmedEmissions
  ];

  const handleRowClick = () => {
    if (isManualEntryEmission) {
      return setSelectedEmission(emission);
    }

    if (!id) {
      return setSelectedRecurringEmission(emission);
    }

    return setSelectedTransaction(emission);
  };

  return (
    <VirtualizedTableRow
      findColumnWidth={findColumnWidth}
      style={{
        ...style
      }}
      idx={idx}
      onClick={() => handleRowClick()}
      tableCells={tableCells}
    />
  );
};

const EmissionsTable = ({
  width,
  emissions,
  setEmissions,
  viewMode,
  tableRef
}) => {
  const { palette } = useTheme();
  const { carbonUnitLabel } = useDisplayMeasurementSystem();
  const updateTransactions = useUpdateTransactions();

  const findInitialSort = () => {
    const highSeverityRows = emissions.filter(({ severity }) => severity > 1);

    if (highSeverityRows.length) {
      return { field: "status", isAsc: false };
    }

    return {
      field: "date",
      isAsc: false
    };
  };

  const history = useHistory();
  const location = useLocation();
  const selectedTransactionId = new URLSearchParams(location.search).get("id");
  const selectedTransaction =
    emissions.find((e) => e.id === selectedTransactionId) ?? null;
  const setSelectedTransaction = (transaction) =>
    transaction
      ? history.replace({ search: `?id=${transaction.id}` })
      : history.replace({ search: "" });

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedOffices, setSelectedOffices] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [selectedEmission, setSelectedEmission] = useState(null);
  const [selectedRecurringEmission, setSelectedRecurringEmission] =
    useState(null);
  const [selectedTransactionIds, setSelectedTransactionIds] = useState([]);
  const [selectedSort, setSelectedSort] = useState(findInitialSort());
  const [searchValue, setSearchValue] = useState("");
  const { isAsc } = selectedSort;

  useEffect(() => {
    const compareIdsAndTransactions = () => {
      return setSelectedTransactionIds((prevSelectedTransactionIds) =>
        prevSelectedTransactionIds.filter((id) =>
          emissions.find((emission) => emission.id === id)
        )
      );
    };

    compareIdsAndTransactions();
  }, [emissions]);

  const checkableTransactions = emissions.filter(
    ({ source, subcategory }) =>
      !(
        ["default-utilities", "recurring", "manual-emission"].includes(
          source
        ) || subcategory === "events"
      )
  );
  const isAnyCheckableTransactions = !!checkableTransactions.length;

  const buildTableColumns = () => {
    const actionsHeader = isAnyCheckableTransactions
      ? [{ minWidth: 70, field: "actions" }]
      : [];

    const checboxHeader = isAnyCheckableTransactions
      ? [{ minWidth: 50, field: "checkbox" }]
      : [];

    return [
      ...checboxHeader,
      { minWidth: 70, field: "date" },
      { minWidth: 70, field: "status", label: "Status" },
      { minWidth: 65, field: "source" },
      {
        minWidth: 100,
        field: viewMode === "scopes" ? "scope" : "subcategory",
        label: "category"
      },
      { minWidth: 70, field: "value", label: "Amount" },
      { minWidth: 150, field: "name", primary: true },
      { minWidth: 40, field: "notes", label: "Notes" },
      { minWidth: 70, field: "tonsCo2e", label: `${carbonUnitLabel} CO2` },
      ...actionsHeader
    ];
  };

  const onHeaderCheck = () => {
    if (selectedTransactionIds.length === checkableTransactions.length) {
      return setSelectedTransactionIds([]);
    }

    const allRowsArray = checkableTransactions.map(({ id }) => id);

    return setSelectedTransactionIds(allRowsArray);
  };

  const onTransactionCheck = (transactionId) => {
    const rowIdx = selectedTransactionIds.findIndex(
      (id) => id === transactionId
    );

    if (rowIdx === -1) {
      return addDataRow(setSelectedTransactionIds, transactionId);
    }

    return removeDataRow(rowIdx, setSelectedTransactionIds)();
  };

  const sortRows = (field) =>
    setSelectedSort(({ isAsc: currentIsAsc }) => ({
      field,
      isAsc: !currentIsAsc
    }));

  const onUpdateBulkTransactions = (updateObj) => {
    updateTransactions(
      selectedTransactionIds.map((id) => ({ id, ...updateObj }))
    );
    setEmissions((prevEmissions) =>
      prevEmissions.map((emission) => {
        if (selectedTransactionIds.includes(emission.id)) {
          return { ...emission, ...updateObj };
        }

        return emission;
      })
    );

    return setSelectedTransactionIds([]);
  };

  const applyFilters = () => {
    const filterBySearch = (emission) => {
      const { name = "", displayName, subcategory } = emission;
      const searchValueLower = searchValue.toLowerCase();
      const stringCompare = (string = "") => {
        if (!string) {
          return false;
        }
        return string.toLowerCase().includes(searchValueLower);
      };

      return (
        stringCompare(name) ||
        stringCompare(displayName) ||
        stringCompare(subcategory)
      );
    };

    const findEmissionsWithTags = (emissionTag, selectedTags) => {
      if (!selectedTags.length) {
        return true;
      }

      const selectedTagsNames = selectedTags.map(({ name }) => name);

      if (Array.isArray(emissionTag)) {
        return !!emissionTag.filter((tagObj) =>
          selectedTagsNames.includes(ucFirstLetters(tagObj.name))
        ).length;
      }

      return selectedTagsNames.includes(ucFirstLetters(emissionTag?.name));
    };

    const filterByEmployees = ({ employees }) =>
      findEmissionsWithTags(employees, selectedEmployees);

    const filterByOffice = ({ office }) =>
      findEmissionsWithTags(office, selectedOffices);

    const filterByVendor = ({ vendorName }) => {
      if (!selectedVendors.length) {
        return true;
      }
      const selectedVendorNames = selectedVendors.map(({ name }) => name);
      return selectedVendorNames.includes(ucFirstLetters(vendorName));
    };

    return emissions.filter((emission) => {
      return (
        filterByEmployees(emission) &&
        filterByOffice(emission) &&
        filterByVendor(emission) &&
        filterBySearch(emission)
      );
    });
  };

  const filteredSummaryTableEmissions = applyFilters();

  const headerCheckboxProps = {
    value: selectedTransactionIds.length === checkableTransactions.length,
    editValue: onHeaderCheck,
    indeterminate:
      !!selectedTransactionIds.length &&
      selectedTransactionIds.length < checkableTransactions.length
  };

  const selectedTransactionStatuses = selectedTransactionIds.map(
    (selectedId) => {
      const { status } = emissions.find(({ id }) => id === selectedId) || {};
      return { id: selectedId, status };
    }
  );
  const areAnySelectedTransactionsConfirmed = selectedTransactionStatuses.find(
    ({ status }) => status === "confirmed"
  );

  const buildFilterPopperProps = () => {
    const populateFilterAutocompletes = (type) => {
      const rowsByFilterType = emissions.filter((row) => row[type]);

      const namesByFilterType = rowsByFilterType.map((row) => {
        const { employees } = row;

        if (employees?.length && type === "employees") {
          return employees.map(({ name }) => name);
        }

        return row[type].name;
      });

      const formattedNames =
        type === "employees"
          ? namesByFilterType.reduce(
              (acc, names = []) => [...acc, ...names],
              []
            )
          : namesByFilterType;

      const namesNoDups = formattedNames.length
        ? [...new Set(formattedNames)]
        : [];

      return namesNoDups
        .filter((name) => name)
        .map((name) => ({ name: ucFirstLetters(String(name)) }));
    };

    const filterRows = [
      {
        icon: faBuilding,
        title: "Offices",
        type: "office",
        autocomplete: {
          availableOptions: populateFilterAutocompletes("office"),
          value: selectedOffices,
          setValue: setSelectedOffices,
          label: "Offices"
        }
      },
      {
        icon: faUserFriends,
        title: "Employees",
        type: "employee",
        autocomplete: {
          availableOptions: populateFilterAutocompletes("employees"),
          value: selectedEmployees,
          setValue: setSelectedEmployees,
          label: "Employees"
        }
      },
      {
        icon: faMoneyBill,
        title: "Vendors",
        type: "vendor",
        autocomplete: {
          availableOptions: [
            ...new Set(
              emissions
                .filter(({ vendorName }) => vendorName)
                .map(({ vendorName }) => ucFirstLetters(vendorName))
            )
          ].map((name) => ({ name })),
          value: selectedVendors,
          setValue: setSelectedVendors,
          label: "Vendors"
        }
      }
    ];

    const resetFilters = () => {
      setSelectedEmployees([]);
      setSelectedOffices([]);
      return setSelectedVendors([]);
    };

    return { filterRows, resetFilters };
  };

  const isFiltered =
    selectedEmployees.length ||
    selectedOffices.length ||
    selectedVendors.length;

  return (
    <>
      {!!(selectedTransaction || selectedRecurringEmission) && (
        <ViewRowTransactionDetails
          transaction={selectedTransaction || selectedRecurringEmission || {}}
          setIsSlideOpen={
            selectedRecurringEmission
              ? setSelectedRecurringEmission
              : setSelectedTransaction
          }
          saveButtonText="Save Changes"
          setEmissions={setEmissions}
        />
      )}
      {!!selectedEmission && (
        <EmissionsVolumeForm
          existingEmission={selectedEmission}
          setDialogOpen={setSelectedEmission}
        />
      )}
      <VirtualizedTable
        tableRef={tableRef}
        width={width}
        rows={sortAndOrderRows(filteredSummaryTableEmissions, selectedSort)}
        filteredRows={filteredSummaryTableEmissions}
        selectedSort={selectedSort}
        sortRows={sortRows}
        isAsc={isAsc}
        RowComponent={EmissionsTableRowComponent}
        rowProps={{
          selectedTransactionIds,
          onTransactionCheck,
          areAnyTransactionsNonRecurring: isAnyCheckableTransactions,
          setEmissions,
          updateTransactions
        }}
        setSelectedTransaction={setSelectedTransaction}
        setSelectedEmission={setSelectedEmission}
        setSelectedRecurringEmission={setSelectedRecurringEmission}
        tableColumns={buildTableColumns()}
        tableTitle={
          isFiltered || searchValue.length
            ? `Filtered Emissions (${filteredSummaryTableEmissions.length})`
            : selectedTransactionIds.length
              ? `Selected Transactions (${selectedTransactionIds.length})`
              : `All Emissions (${filteredSummaryTableEmissions.length})`
        }
        viewMode={viewMode}
        headerCheckboxProps={headerCheckboxProps}
        tableAction={
          <Grid container>
            <Grid item>
              <Tooltip title="Filter Emissions">
                <span>
                  <IconButton
                    onClick={(e) =>
                      setAnchorEl((currentAnchorEl) =>
                        currentAnchorEl ? null : e.currentTarget
                      )
                    }
                  >
                    {isFiltered ? (
                      <Badge
                        variant="dot"
                        color="secondary"
                        style={{ zIndex: 0 }}
                      >
                        <FilterListIcon />
                      </Badge>
                    ) : (
                      <FilterListIcon />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <FilterPopper
                open={!!anchorEl}
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                filterPopperProps={{
                  ...buildFilterPopperProps(),
                  title: "Filter Emissions"
                }}
              />
            </Grid>
            <Grid item>
              <NullableInput
                value={searchValue}
                setValue={setSearchValue}
                label="Search Emissions"
              />
            </Grid>
            {!!selectedTransactionIds.length && (
              <Grid item>
                <Tooltip
                  title={
                    areAnySelectedTransactionsConfirmed
                      ? "You can't accept default emissions for confirmed transactions"
                      : `Accept Default Emissions for ${
                          selectedTransactionIds.length
                        } transaction${letterSBoolean(selectedTransactionIds)}`
                  }
                >
                  <span>
                    <IconButton
                      size="large"
                      disabled={areAnySelectedTransactionsConfirmed}
                    >
                      <CheckCircleIcon
                        onClick={() =>
                          onUpdateBulkTransactions({ status: "confirmed" })
                        }
                        style={{
                          color: hexToRgba(
                            palette.success.main,
                            areAnySelectedTransactionsConfirmed ? 0.5 : 1
                          )
                        }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={`Archive ${
                    selectedTransactionIds.length
                  } transaction${letterSBoolean(selectedTransactionIds)}`}
                >
                  <span>
                    <IconButton size="large">
                      <DeleteIcon
                        onClick={() =>
                          onUpdateBulkTransactions({ archived: true })
                        }
                        style={{ color: palette.error.main }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        }
      />
    </>
  );
};

const PrimaryViewTable = ({ emissions, viewMode, setEmissions }) => {
  const [tableWidth, setTableWidth] = useState(0);

  const tableRef = useRef(null);
  useEffect(() => {
    if (tableRef.current) {
      setTableWidth(tableRef.current?.offsetWidth);
    }
  }, [tableRef, setTableWidth]);

  return (
    <EmissionsTable
      tableRef={tableRef}
      width={tableWidth}
      emissions={emissions}
      viewMode={viewMode}
      setEmissions={setEmissions}
    />
  );
};
export default PrimaryViewTable;
